import React, { useRef, useEffect, useCallback, useReducer, useState } from 'react';
import {
  Box, Typography, Dialog, Slider, Button, IconButton, CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import DeleteIcon from '@mui/icons-material/Delete';
import AvatarEditor from 'react-avatar-editor';
import { toast } from 'sonner';
import api from '../../api';
import { DEFAULT_AVATAR_STORAGE_KEY, DEFAULT_AVATAR_URL, fetchAndCacheDefaultAvatar } from '../../pages/utils/avatarCache';

const initialState = {
  image: null,
  currentImage: null,
  isEditing: false,
  scale: 1,
  isDragging: false,
  isLoading: false,
  imageSaving: false,
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_IMAGE': return { ...state, image: action.payload };
    case 'SET_CURRENT_IMAGE': return { ...state, currentImage: action.payload };
    case 'SET_EDITING': return { ...state, isEditing: action.payload };
    case 'SET_SCALE': return { ...state, scale: action.payload };
    case 'SET_DRAGGING': return { ...state, isDragging: action.payload };
    case 'SET_LOADING': return { ...state, isLoading: action.payload };
    case 'SET_IMAGE_SAVING': return { ...state, imageSaving: action.payload };
    case 'RESET': return { ...initialState, image: state.image };
    default: return state;
  }
}

const CircularLogoUpload = ({ previewUrl, onLogoChange, maxFileSize }) => {
  const [state, dispatch] = useReducer(reducer, { ...initialState, image: localStorage.getItem(DEFAULT_AVATAR_STORAGE_KEY) || DEFAULT_AVATAR_URL });
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);
  const [defaultAvatarLoaded, setDefaultAvatarLoaded] = useState(false);

  const fetchAndStoreDefaultAvatar = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const avatarUrl = await fetchAndCacheDefaultAvatar();
      dispatch({ type: 'SET_IMAGE', payload: avatarUrl });
      setDefaultAvatarLoaded(true);
    } catch (error) {
      console.error("Error in avatar fetch:", error);
      dispatch({ type: 'SET_IMAGE', payload: DEFAULT_AVATAR_URL });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  useEffect(() => {
    if (!previewUrl) {
      if (!defaultAvatarLoaded) {
        fetchAndStoreDefaultAvatar();
      }
      return;
    }

    const img = new Image();
    img.onload = () => dispatch({ type: 'SET_IMAGE', payload: previewUrl });
    img.onerror = () => {
      if (localStorage.getItem(DEFAULT_AVATAR_STORAGE_KEY)) {
        dispatch({ type: 'SET_IMAGE', payload: localStorage.getItem(DEFAULT_AVATAR_STORAGE_KEY) });
      } else {
        fetchAndStoreDefaultAvatar();
      }
    };
    img.src = previewUrl;
  }, [previewUrl, defaultAvatarLoaded, fetchAndStoreDefaultAvatar]);

  const handleFileSelect = useCallback((file) => {
    if (!file) return;
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload only PNG or JPEG files");
      return;
    }
    if (file.size > maxFileSize * 1024 * 1024) {
      toast.error(`File size should not exceed ${maxFileSize}MB`);
      return;
    }
    dispatch({ type: 'SET_LOADING', payload: true });
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target.result;
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        dispatch({ type: 'SET_LOADING', payload: false });
        dispatch({ type: 'SET_CURRENT_IMAGE', payload: imageUrl });
        dispatch({ type: 'SET_EDITING', payload: true });
      };
      img.onerror = () => {
        dispatch({ type: 'SET_LOADING', payload: false });
        toast.error("Failed to load image. Please try another image.");
      };
      img.src = imageUrl;
    };
    reader.onerror = () => {
      dispatch({ type: 'SET_LOADING', payload: false });
      toast.error("Failed to read file. Please try again.");
    };
    reader.readAsDataURL(file);
  }, [maxFileSize]);

  const handleSave = useCallback(async () => {
    if (!editorRef.current) return;
    dispatch({ type: 'SET_IMAGE_SAVING', payload: true });
    try {
      const canvas = editorRef.current.getImageScaledToCanvas();
      canvas.toBlob(async (blob) => {
        if (!blob) {
          toast.error("Failed to process image.");
          dispatch({ type: 'SET_IMAGE_SAVING', payload: false });
          return;
        }
        const file = new File([blob], 'profile-picture.png', { type: 'image/png' });
        const formData = new FormData();
        formData.append("profile", file);
        const token = JSON.parse(localStorage.getItem("token"))?.token;
        try {
          const response = await api.put(`/users/update-profile`, formData, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          const updatedUser = response.data.user;
          localStorage.setItem("User", JSON.stringify(updatedUser));
          onLogoChange?.({ file, previewUrl: updatedUser.ProfilePicture, changed: true });
          dispatch({ type: 'SET_IMAGE', payload: updatedUser.ProfilePicture });
          dispatch({ type: 'SET_EDITING', payload: false });
          dispatch({ type: 'SET_IMAGE_SAVING', payload: false });
          toast.success("Profile picture updated successfully!");
          setTimeout(() => window.location.reload(), 1500);
        } catch (uploadError) {
          toast.error(uploadError.response?.data?.message || "Upload failed. Try again.");
          dispatch({ type: 'SET_IMAGE_SAVING', payload: false });
        }
      }, 'image/png', 0.95);
    } catch (err) {
      toast.error("Unexpected error. Try again.");
      dispatch({ type: 'SET_IMAGE_SAVING', payload: false });
    }
  }, [onLogoChange]);

  const handleRemoveLogo = useCallback(async () => {
    dispatch({ type: 'SET_IMAGE_SAVING', payload: true });
    const token = JSON.parse(localStorage.getItem("token"))?.token;
    const formData = new FormData();
    formData.append("ProfilePicture", "null");
    try {
      const response = await api.put(`/users/update-profile`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const updatedUser = response.data.user;
      localStorage.setItem("User", JSON.stringify(updatedUser));
      
      const cachedDefault = localStorage.getItem(DEFAULT_AVATAR_STORAGE_KEY) || DEFAULT_AVATAR_URL;
      onLogoChange?.({ file: null, previewUrl: null, changed: true });
      dispatch({ type: 'SET_IMAGE', payload: cachedDefault });
      dispatch({ type: 'SET_CURRENT_IMAGE', payload: null });
      dispatch({ type: 'SET_EDITING', payload: false });
      dispatch({ type: 'SET_IMAGE_SAVING', payload: false });
      toast.success("Profile picture removed successfully!");
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to remove profile picture.");
      dispatch({ type: 'SET_IMAGE_SAVING', payload: false });
    }
  }, [onLogoChange]);

  const handleCancelEdit = useCallback(() => {
    dispatch({ type: 'SET_EDITING', payload: false });
    dispatch({ type: 'SET_CURRENT_IMAGE', payload: null });
    dispatch({ type: 'SET_SCALE', payload: 1 });
    dispatch({ type: 'SET_DRAGGING', payload: false });
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    dispatch({ type: 'SET_DRAGGING', payload: false });
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  useEffect(() => {
    if (!previewUrl && !defaultAvatarLoaded) {
      fetchAndStoreDefaultAvatar();
    }
  }, [previewUrl, defaultAvatarLoaded, fetchAndStoreDefaultAvatar]);

  return (
    <>
      <Box sx={{ position: 'relative', width: 160, height: 160 }}>
        <Box
          role="button"
          tabIndex={0}
          sx={{
            width: 160,
            height: 160,
            bgcolor: 'grey.200',
            borderRadius: '50%',
            backgroundImage: `url(${state.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: state.isDragging ? '2px dashed #02042D' : '2px dashed transparent',
            overflow: 'hidden',
            transition: 'all 0.2s ease-in-out',
            '&:hover .drag-overlay': { opacity: 1 },
          }}
          onClick={() => fileInputRef.current?.click()}
          onDragEnter={(e) => { e.preventDefault(); dispatch({ type: 'SET_DRAGGING', payload: true }); }}
          onDragLeave={(e) => { e.preventDefault(); dispatch({ type: 'SET_DRAGGING', payload: false }); }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          {state.isLoading && (
            <Box sx={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', zIndex: 10 }}>
              <CircularProgress size={40} sx={{ color: '#02042D' }} />
            </Box>
          )}
          <Box className="drag-overlay" sx={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(2,4,45,0.1)', borderRadius: '50%', opacity: 0, transition: 'opacity 0.2s ease-in-out' }} />
        </Box>

        <IconButton
          sx={{ position: 'absolute', bottom: 0, right: 0, bgcolor: 'background.paper', boxShadow: '0px 2px 4px rgba(0,0,0,0.1)', '&:hover': { bgcolor: 'grey.300' } }}
          onClick={() => {
            dispatch({ type: 'SET_CURRENT_IMAGE', payload: state.image });
            dispatch({ type: 'SET_EDITING', payload: true });
          }}
          disabled={state.isLoading}
          title="Edit profile picture"
        >
          {state.isLoading ? <CircularProgress size={16} /> : <EditIcon fontSize="small" />}
        </IconButton>
      </Box>

      <input type="file" hidden accept="image/png,image/jpeg" ref={fileInputRef} onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])} />

      <Dialog open={state.isEditing} onClose={handleCancelEdit} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px', bgcolor: '#FFFFFF' } }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Edit Image</Typography>
            {state.image !== (localStorage.getItem(DEFAULT_AVATAR_STORAGE_KEY) || DEFAULT_AVATAR_URL) && (
              <Button variant="outlined" startIcon={<DeleteIcon />} onClick={handleRemoveLogo} disabled={state.imageSaving} sx={{ color: '#d32f2f', borderColor: '#ffcdd2', textTransform: 'none', '&:hover': { backgroundColor: '#ffebee', borderColor: '#ef9a9a' } }}>
                Remove
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, position: 'relative' }}>
            {state.currentImage && (
              <AvatarEditor ref={editorRef} image={state.currentImage} width={250} height={250} border={50} borderRadius={125} scale={state.scale} rotate={0} />
            )}
          </Box>
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontWeight: 500 }}>Zoom</Typography>
              <Typography sx={{ fontSize: '0.875rem' }}>{Math.round(state.scale * 100)}%</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ZoomOutIcon />
              <Slider value={state.scale} min={1} max={3} step={0.1} onChange={(_, value) => typeof value === 'number' && dispatch({ type: 'SET_SCALE', payload: value })} disabled={state.imageSaving} />
              <ZoomInIcon />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={handleCancelEdit} disabled={state.imageSaving}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={state.imageSaving} sx={{ backgroundColor: '#02042D', '&:hover': { backgroundColor: '#080A3C' } }}>
              {state.imageSaving ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                  <span>Saving...</span>
                </Box>
              ) : (
                "Save Changes"
              )}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default CircularLogoUpload;
